import { useState } from 'react';
import { Link } from 'react-router';
import type { Finding, Severity } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { useAcrossEngagements } from '../../lib/useAcrossEngagements';

const LEVELS: (Severity | 'All')[] = ['All', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];
const ORDER: Severity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFORMATIONAL'];

const label = (s: Severity | 'All') =>
  s === 'All' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase();

export function FindingsPage() {
  const [level, setLevel] = useState<Severity | 'All'>('All');
  const { items, isLoading, isError, error, isPartial, engagementCount } =
    useAcrossEngagements<Finding>('findings', api.listFindings);

  const rows = items
    .filter(({ item }) => level === 'All' || item.severity === level)
    .sort((a, b) => ORDER.indexOf(a.item.severity) - ORDER.indexOf(b.item.severity));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Workspace</span>
          <h1>Findings</h1>
        </div>
      </div>

      <p className="muted" style={{ maxWidth: '68ch', marginTop: -8 }}>
        Everything reported across every engagement. A finding stays <strong>unverified</strong>{' '}
        until something reproduces it — a scanner alert is a hypothesis, not a fact.
      </p>

      <div className="filters" role="group" aria-label="Filter by severity">
        {LEVELS.map((l) => (
          <button
            key={l}
            type="button"
            className={`filter ${level === l ? 'is-on' : ''}`}
            aria-pressed={level === l}
            onClick={() => setLevel(l)}
          >
            {label(l)}
          </button>
        ))}
      </div>

      {isPartial ? (
        <p className="demo-notice" role="status">
          Some engagements could not be read, so this list may be incomplete.
        </p>
      ) : null}

      <section className="card panel table-wrap">
        {isLoading ? (
          <p className="muted">Loading findings…</p>
        ) : isError ? (
          <p className="login-error" role="alert">
            {error instanceof ApiError ? error.message : 'Could not load findings.'}
          </p>
        ) : rows.length === 0 ? (
          <div className="empty">
            <p>{items.length === 0 ? 'No findings yet.' : `No ${label(level)} findings.`}</p>
            <p className="muted">
              {engagementCount === 0
                ? 'Create an engagement, define its scope and run a scan.'
                : 'Findings appear here once a scan has run against an approved scope.'}
            </p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Finding</th>
                <th>Where</th>
                <th>Engagement</th>
                <th>Status</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ item, engagement }) => (
                <tr key={item.id}>
                  <td className={`sev-${item.severity.toLowerCase()}`}>{label(item.severity)}</td>
                  <td>{item.title}</td>
                  <td className="mono muted" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                    {item.evidence?.url ?? '—'}
                  </td>
                  <td>
                    <Link to={`/app/engagements/${engagement.id}`} className="table-link">
                      {engagement.name}
                    </Link>
                  </td>
                  <td className="muted">{item.status}</td>
                  <td className="muted">{new Date(item.lastSeenAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
